import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionItem,
  Alert,
  BusinessIcon,
  Button,
  Chip,
  Divider,
  PersonIcon,
  SecurityIcon,
  Spinner,
  Text,
} from '@billda/design-system';
import { getMe, type MeResponse } from '../../../features/users/api/userApi';
import { useAuthStore } from '../../../features/auth/stores/authStore';
import { Auth } from '@billda/permissions-ui';

export const UserDataSection: React.FC = () => {
  const token = useAuthStore(state => state.token);
  const [userData, setUserData] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current organization from JWT token
  const getCurrentOrgFromToken = () => {
    if (!token) {
      return null;
    }
    const parsed = Auth.parseToken(token);
    return parsed?.currentOrganisationId ?? null;
  };

  const currentOrgFromToken = getCurrentOrgFromToken();

  // Organize organizations with tagged ones at the top
  const organizeOrganizations = (orgs: any[], databaseInfo: any) => {
    if (!orgs) {
      return [];
    }

    const lastSelectedId = databaseInfo.lastSelectedOrganisation?.id;

    return [...orgs].sort((a, b) => {
      const aIsCurrentFromToken = a.id === currentOrgFromToken;
      const bIsCurrentFromToken = b.id === currentOrgFromToken;
      const aIsLastSelected = a.id === lastSelectedId;
      const bIsLastSelected = b.id === lastSelectedId;

      // Current from token takes priority
      if (aIsCurrentFromToken && !bIsCurrentFromToken) {
        return -1;
      }
      if (!aIsCurrentFromToken && bIsCurrentFromToken) {
        return 1;
      }

      // Then last selected
      if (aIsLastSelected && !bIsLastSelected) {
        return -1;
      }
      if (!aIsLastSelected && bIsLastSelected) {
        return 1;
      }

      // Finally alphabetical by name
      return a.name.localeCompare(b.name);
    });
  };

  // wrap in usecallback to avoid re-creating the function on every render
  const fetchUserData = React.useCallback(async () => {
    if (!token) {
      setError('No authentication token available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getMe();
      setUserData(response);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch user data'
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUserData();
    }
  }, [token, fetchUserData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner />
        <Text variant="body2" className="ml-4">
          Loading user data...
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="mb-2">
        <div className="flex items-center justify-between gap-3">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={fetchUserData}>
            Retry
          </Button>
        </div>
      </Alert>
    );
  }

  if (!userData) {
    return (
      <Alert severity="info" className="mb-2">
        No user data available
      </Alert>
    );
  }

  const { databaseInfo } = userData;

  return (
    <div>
      <Text as="h6" variant="h6" className="mb-2">
        User Database Information
      </Text>

      {/* User Information */}
      <Accordion className="mb-4" defaultValue={['user-info']}>
        <AccordionItem
          value="user-info"
          title={
            <span className="flex items-center gap-2">
              <PersonIcon aria-hidden="true" className="size-5" />
              User Information
            </span>
          }
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Text variant="body2" color="secondary">
                Name
              </Text>
              <Text variant="body1" className="mb-2">
                {databaseInfo.givenName} {databaseInfo.familyName}
              </Text>

              <Text variant="body2" color="secondary">
                Email
              </Text>
              <Text variant="body1" className="mb-2">
                {databaseInfo.email}
              </Text>

              <Text variant="body2" color="secondary">
                ID
              </Text>
              <Text variant="body2" className="font-mono">
                {databaseInfo.id}
              </Text>
            </div>

            <div>
              <Text variant="body2" color="secondary" className="mb-2">
                Phone Numbers ({databaseInfo.phoneNumbers.length})
              </Text>
              {databaseInfo.phoneNumbers.length > 0 ? (
                <div className="mt-2 overflow-hidden rounded-lg border border-border-default">
                  <table className="w-full type-body-md">
                    <thead>
                      <tr className="border-b border-border-default text-left">
                        <th className="px-3 py-1.5 font-medium">Number</th>
                        <th className="px-3 py-1.5 font-medium">Is Primary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {databaseInfo.phoneNumbers.map(
                        (phone: any, index: number) => (
                          <tr
                            key={phone.id || index}
                            className="border-b border-border-default last:border-b-0"
                          >
                            <td className="px-3 py-1.5">
                              {phone.phoneNumber ||
                                phone.number ||
                                phone.value ||
                                `Phone ${index + 1}`}
                            </td>
                            <td className="px-3 py-1.5">
                              {phone.isPrimary ||
                              phone.primary ||
                              phone.isDefault
                                ? 'Yes'
                                : 'No'}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Text variant="body2" color="secondary" className="italic">
                  No phone numbers
                </Text>
              )}
            </div>
          </div>
        </AccordionItem>
      </Accordion>

      {/* All Organizations */}
      {databaseInfo.organisations.length > 0 && (
        <Accordion className="mb-4" defaultValue={['all-orgs']}>
          <AccordionItem
            value="all-orgs"
            title={
              <span className="flex items-center gap-2">
                <BusinessIcon aria-hidden="true" className="size-5" />
                All Organizations ({databaseInfo.organisations.length})
              </span>
            }
          >
            {organizeOrganizations(
              databaseInfo.organisations,
              databaseInfo
            ).map((org, index) => (
              <div key={org.id}>
                <div className="mb-4 rounded-lg border border-border-default bg-surface-raised p-4">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <Text variant="caption" className="font-semibold">
                      {org.name}
                    </Text>
                    {org.id === currentOrgFromToken && (
                      <Chip
                        label="Current (from token)"
                        size="sm"
                        color="primary"
                      />
                    )}
                    {org.id === databaseInfo.lastSelectedOrganisation?.id && (
                      <Chip label="Last Selected" size="sm" color="secondary" />
                    )}
                  </div>
                  <Text
                    variant="caption"
                    color="secondary"
                    className="mb-2 block font-mono"
                  >
                    ID: {org.id}
                  </Text>

                  {org.roles.length > 0 && (
                    <div className="mt-4">
                      <Text variant="body2" color="secondary" className="mb-2">
                        Roles & Permissions
                      </Text>
                      {org.roles.map((role: any) => (
                        <div
                          key={role.id}
                          className="mb-2 rounded-lg border border-border-default p-2"
                        >
                          <div className="mb-2 flex items-center gap-2">
                            <SecurityIcon
                              aria-hidden="true"
                              className="size-4"
                            />
                            <Text variant="body2">{role.name}</Text>
                            <Text variant="caption" color="secondary">
                              ({role.id})
                            </Text>
                          </div>

                          <Accordion>
                            <AccordionItem
                              value={`permissions-${role.id}`}
                              title={
                                <Text variant="caption">
                                  Permissions ({role.permissions.length})
                                </Text>
                              }
                            >
                              {role.permissions.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {role.permissions.map((permission: any) => (
                                    <Chip
                                      key={permission.id}
                                      label={permission.name}
                                      size="sm"
                                      variant="outlined"
                                      title={`Permission ID: ${permission.id}`}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <Text
                                  variant="caption"
                                  color="secondary"
                                  className="italic"
                                >
                                  No permissions assigned to this role
                                </Text>
                              )}
                            </AccordionItem>
                          </Accordion>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {index <
                  organizeOrganizations(
                    databaseInfo.organisations,
                    databaseInfo
                  ).length -
                    1 && <Divider className="my-4" />}
              </div>
            ))}
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};
